import React from "react";
import { Box, Button, CircularProgress, Alert } from "@mui/material";

/**
 * Reusable component for form action buttons and status messages
 *
 * @param {Object} props - Component props
 * @param {Function} props.onSubmit - Handler for submit button click
 * @param {Function} props.onReset - Handler for reset button click (optional)
 * @param {boolean} props.isSubmitting - Whether form is currently submitting
 * @param {string} props.submitLabel - Label for submit button (defaults based on isEdit)
 * @param {string} props.resetLabel - Label for reset button
 * @param {Function} props.onDelete - Handler for delete button click (optional)
 * @param {string} props.deleteLabel - Label for delete button
 * @param {string} props.successMessage - Success message to display
 * @param {string} props.errorMessage - Error message to display
 * @param {boolean} props.isEdit - Whether form is in edit mode (affects default submit label)
 */
const FormActionBar = ({
  onSubmit,
  onReset,
  isSubmitting = false,
  submitLabel,
  resetLabel = "Reset",
  onDelete,
  deleteLabel = "Delete",
  successMessage = "",
  errorMessage = "",
  isEdit = false,
  className = "",
}) => {
  // Use provided label or default based on isEdit state
  const buttonText = submitLabel || (isEdit ? "Update" : "Save");

  return (
    <Box
      className={`form-action-bar ${className}`}
      sx={{
        mt: 5,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "flex-start" },
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={isSubmitting}
          sx={{
            minWidth: 100,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {buttonText}
          {isSubmitting && (
            <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
          )}
        </Button>

        {/* Only render reset button if handler is provided */}
        {onReset && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={onReset}
            disabled={isSubmitting}
            sx={{ minWidth: 100 }}
          >
            {resetLabel}
          </Button>
        )}

        {onDelete && (
          <Button
            variant="outlined"
            color="error"
            onClick={onDelete}
            disabled={isSubmitting}
            sx={{ minWidth: 100 }}
          >
            {deleteLabel}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minHeight: 50, // Maintain consistent height even when empty
        }}
      >
        {successMessage && (
          <Alert severity="success" variant="outlined" sx={{ py: 0 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Hidden placeholder to maintain layout when no messages are present */}
        {!successMessage && !errorMessage && (
          <Box sx={{ visibility: "hidden", height: 0 }}>
            <Alert severity="info" variant="outlined">
              Placeholder
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FormActionBar;
